import store, { getChat, setChat, getMyUser, getMyUserId } from '@/store'
import { fetchBubble } from '@/api/chat'
import Pusher from 'pusher-js'
import { createLogger } from '@/helpers/log'
import app from '@/app'
import posthog from 'posthog-js'
import {
	collectChunks,
	unbindChannel,
	setChunkLogger,
} from '@/helpers/pusherChunks'
const log = createLogger('Pusher')

// Route chunk failures into the exportable diagnostic log AND the console:
// createLogger's console path is gated behind console.prontoCapturing, but a
// dropped chunk is a user-visible missing message, not a debug detail.
setChunkLogger((...args) => {
	console.warn('[Pusher]', ...args)
	log(...args)
})

// No global subscription state needed — each ChatPanel manages its own channel lifecycle

// chunk reassembly + channel-scoped binds live in a dependency-free module so
// the wrappermanager.js bundle can share them
export {
	collectChunks,
	bindChannelEventWithChunking,
	bindChannelAppEvents,
	bindChannelRestEvents,
	bindChannelClientEvents,
	getBindEpoch,
	bumpBindEpoch,
	unbindChannel,
} from '@/helpers/pusherChunks'

export const bindEventWithChunking = (event, handler) => {
	const pusher = getPusherInstance()
	if (!pusher) return
	pusher.unbind(event)
	pusher.bind(event, handler)
	const chunkedEvent = `${event}-chunked`
	pusher.unbind(chunkedEvent)
	pusher.bind(chunkedEvent, collectChunks(handler))
}

export const bindEvents = (prefix, eventsByName) => {
	Object.entries(eventsByName).forEach(([event, handler]) => {
		bindEventWithChunking(`${prefix}\\${event}`, handler)
	})
}

export const bindAppEvents = (eventsByName) => {
	bindEvents('App\\Events', eventsByName)
}

export const connectToPusher = () => {
	const { buildNumber, device_type } = window.appConfig
	const version = `${device_type || 'WEB'}/${buildNumber || 1}/0`
	const instance = new Pusher(window.appConfig.pusherAppKey, {
		cluster: 'mt1',
		authEndpoint: '/api/v1/pusher.auth',
		encrypted: true,
		auth: {
			headers: {
				Authorization: `Bearer ${store.auth.api_token}`,
				'X-version': version,
			},
		},
	})
	instance.connection?.bind?.('error', (error) => {
		posthog.capture('pusher_connection_error', error)
		log('connection error', error, localStorage?.pusherTransportTLS)
	})
	instance.connection?.bind?.('state_change', (states) => {
		posthog.capture('pusher_state_change', states)
		log('state_change', states, localStorage?.pusherTransportTLS)
	})
	pusherData.instance = instance
	EventBus.$emit('pusherInitialized', instance)
	return instance
}

const pusherData = {}
// Map<channelName, Promise<Channel>> — tracks in-flight private bubble subscriptions
const pendingSubscriptions = new Map()

export const getPusherInstance = () => {
	return pusherData.instance
}

export const isSubscriptionPending = (channelName) => {
	return pendingSubscriptions.has(channelName)
}

export const getPendingSubscription = (channelName) => {
	return pendingSubscriptions.get(channelName)
}

// Ref-counting for shared channels — prevents one ChatPanel's unload from
// killing a channel that another ChatPanel just subscribed to (e.g. thread
// sidebar → full-screen transition).
const channelRefCounts = new Map()

export const getChannelRefCount = (channelName) =>
	channelRefCounts.get(channelName) || 0

export const retainChannel = (channelName) => {
	const count = (channelRefCounts.get(channelName) || 0) + 1
	channelRefCounts.set(channelName, count)
	log('retain channel', channelName, 'refCount:', count)
}

export const releaseChannel = (channelName) => {
	const count = (channelRefCounts.get(channelName) || 0) - 1
	if (count <= 0) {
		channelRefCounts.delete(channelName)
		log('release channel (last ref), unsubscribing', channelName)
		unsubscribeFromChannel(channelName)
	} else {
		channelRefCounts.set(channelName, count)
		log('release channel', channelName, 'refCount:', count)
	}
}

export const debugPusher = () => {
	const pusher = getPusherInstance()
	if (!pusher) {
		console.warn('No Pusher instance')
		return
	}
	const channels = pusher.allChannels()
	const report = {
		connectionState: pusher.connection?.state,
		channels: channels.map((ch) => {
			const bindings = {}
			let totalBindings = 0
			const raw = ch.callbacks?._callbacks || {}
			for (const key of Object.keys(raw)) {
				const name = key.startsWith('_') ? key.slice(1) : key
				const count = raw[key]?.length || 0
				bindings[name] = count
				totalBindings += count
			}
			return {
				name: ch.name,
				subscribed: ch.subscribed,
				subscriptionPending: ch.subscriptionPending,
				refCount: channelRefCounts.get(ch.name) || 0,
				pendingSubscription: pendingSubscriptions.has(ch.name),
				totalBindings,
				bindings,
			}
		}),
		orphanedRefs: [...channelRefCounts.entries()]
			.filter(([name]) => !channels.some((ch) => ch.name === name))
			.map(([name, count]) => ({ name, count })),
		pendingSubscriptions: [...pendingSubscriptions.keys()],
	}
	console.debug('[Pusher Debug]', report)
	// also return for console inspection
	return report
}

// Expose on window for console access
if (typeof window !== 'undefined') {
	window.debugPusher = debugPusher
}

export const PusherPlugin = {
	install: function (app) {
		pusherData.instance = connectToPusher()
		Object.assign(app.config.globalProperties, {
			$pusher: pusherData.instance,
		})
	},
}

export const initPusher = () => {
	//connect to pusher and listen for specific events we need...
	app.use(PusherPlugin)
}

export const forceReloadPusherInstance = () => {
	log('FORCE RELOADING PUSHER INSTANCE...')
	pendingSubscriptions.clear()
	channelRefCounts.clear()
	connectToPusher()
	EventBus.$emit('reloadPusherInstance')
	log('FORCE RELOAD OF PUSHER INSTANCE DONE')
}

export const unsubscribeFromChannel = (channelName) => {
	// A subscribe is in flight for this channel but hasn't resolved yet, so it
	// hasn't called retainChannel. Leave it alone — unsubscribing a pending
	// subscription makes pusher force-cancel it mid-flight, so
	// subscription_succeeded never fires and the subscriber (connectPusher)
	// hangs until its 10s timeout. Once the subscribe settles, its .finally
	// clears pendingSubscriptions and the normal retain/release accounting
	// tears the channel down if nothing retained it.
	if (pendingSubscriptions.has(channelName)) {
		log('skipping unsubscribe, subscription in flight', channelName)
		return
	}
	log('unsubscribing from channel', channelName)
	// If another consumer (e.g. ChatPanel) still holds a ref, skip the
	// actual unsubscribe so we don't kill their connection.
	if (channelRefCounts.has(channelName)) {
		log(
			'skipping unsubscribe, channel still has active refs',
			channelName,
			channelRefCounts.get(channelName)
		)
		return
	}
	const pusher = getPusherInstance()
	if (!pusher) return
	pusher.unsubscribe(channelName)
	// after unsubscription attempt, check to see if pusher is still hanging on to channel
	const channel = pusher.channel(channelName)
	if (channel) {
		log(
			'tried to unsubscribe but channel is persisting. Now force unsubscribing...',
			channel
		)
		// this is hacky logic to clean up oprhaned channel
		channel.subscribed = true
		channel.subscriptionPending = false
		channel.subscriptionCancelled = false
		pusher.unsubscribe(channel.name)
		log('force unsubscribe complete', channel)
	}
}

// Note: ref-counted channels (those with active retainChannel refs) will
// survive this sweep — unsubscribeFromChannel checks channelRefCounts.
export const unsubscribeFromAllPrivateBubbleChannels = () => {
	const pusher = getPusherInstance()
	if (!pusher) return
	pusher
		.allChannels()
		.filter((channel) => channel.name?.startsWith('private-bubble'))
		.map((channel) => channel.name)
		.forEach(unsubscribeFromChannel)
}

export const subscribeToPrivateBubble = (bubbleId) => {
	const chat = getChat(bubbleId)
	const pusher = getPusherInstance()
	// chat is {} for an uncached chat (getChat gotcha) and passes this guard
	// ON PURPOSE: subscribing with an undefined channelcode 403s, and the
	// subscription_error handler below recovers by fetching bubble.info and
	// resubscribing with the real channelcode. threadPreviewPusher's
	// cold-cache fall-through relies on that path — don't "fix" this to
	// !chat?.id, it turns the recovery into an immediate rejection.
	if (!chat || !pusher) return Promise.reject(null)

	const channelcode =
		getChat(chat.parent_chat_id)?.channelcode || chat.channelcode
	const channelName = `private-bubble.${bubbleId}.${channelcode}`

	// If a subscription is already in-flight for this exact channel name,
	// return the existing promise instead of re-subscribing (which would
	// call channel.unbind() and destroy the first caller's handlers).
	const existing = pendingSubscriptions.get(channelName)
	if (existing) {
		log('subscribe already in-flight, reusing promise', channelName, bubbleId)
		return existing
	}

	// Fast path: already subscribed
	const existingChannel = pusher.channel(channelName)
	if (existingChannel?.subscribed) {
		log('subscribe skipped, already subscribed', channelName, bubbleId)
		return Promise.resolve(existingChannel)
	}

	log('subscribe initiating', channelName, bubbleId)
	const promise = doSubscribeToPrivateBubble(bubbleId, channelName, channelcode)
		.then((channel) => {
			log('subscribe succeeded', channelName, bubbleId)
			return channel
		})
		.catch((error) => {
			log('subscribe failed', channelName, bubbleId, error)
			throw error
		})
		.finally(() => {
			pendingSubscriptions.delete(channelName)
		})

	pendingSubscriptions.set(channelName, promise)
	return promise
}

const doSubscribeToPrivateBubble = (bubbleId, channelName, channelcode) => {
	return new Promise((resolve, reject) => {
		const pusher = getPusherInstance()
		if (!pusher) return reject(null)

		const channel = pusher.subscribe(channelName)

		//unbind any orphaned events (and bump the bind epoch with it)
		unbindChannel(channel)

		if (channel.subscribed) {
			return resolve(channel)
		}
		channel.bind('pusher:subscription_succeeded', () => {
			log('private.bubble subscription succeeded', channelName, bubbleId)
			resolve(channel)
		})

		channel.bind('pusher:subscription_error', async (error) => {
			log('subscribe error', channelName, error.status, error.error)
			if (error.status === 403) {
				// this means one of two possible scenarios:
				//  1. user has been removed from this channel so we need to bail
				//  2. we have a bad cached channelcode, need to fetch the new one
				log('fetching bubble.info to check for channelcode...')
				try {
					// TODO: swap this over to fetchChat
					const { ok, bubble } = await fetchBubble(bubbleId)
					if (!ok) {
						log('error fetching bubble.info')
						reject(error)
						return
					}
					setChat(bubble)
					log('new channelcode:', bubble.channelcode)
					if (bubble.channelcode !== channelcode) {
						log('channel code changed! attempting to resubscribe...')
						try {
							const channel = await subscribeToPrivateBubble(bubbleId)
							log(
								'successfully resubscribed to private bubble channel!',
								bubbleId,
								channelcode
							)
							resolve(channel)
						} catch (e) {
							reject(e)
						}
					} else {
						log('already had correct channelcode, so bailing')
						reject(error)
					}
				} catch (e) {
					console.error(e)
					reject(e)
				}
			} else {
				// non-403: no recovery path, surface to caller so the pending
				// subscription promise settles instead of hanging forever
				reject(error)
			}
		})
	})
}

const getPresenceChannelName = () => {
	const user = getMyUser()
	if (!user) return null
	return `presence-user.${user.id}`
}

export const getPresenceChannel = () => {
	const channelName = getPresenceChannelName()
	if (!channelName) return null
	const pusher = getPusherInstance()
	return pusher.channel(channelName)
}

export const subscribeToPresence = () => {
	const user = getMyUser()
	const { wrapper, embedded } = store.config
	if (!user || (!user.acceptedtos && (wrapper || embedded))) {
		log('presence', 'no user or no acceptedtos')
		store.userIsActive = false
		return
	}

	const pusher = getPusherInstance()
	let channel = getPresenceChannel()

	if (channel?.subscribed) {
		store.userIsActive = true
		return
	}
	if (channel?.subscriptionPending) return

	const channelName = getPresenceChannelName()
	log('presence', 'not subscribed, attempting to subscribe...', channelName)
	channel = pusher.subscribe(channelName)
	channel.bind('pusher:subscription_succeeded', () => {
		log('presence', 'successfully subscribed')
		store.userIsActive = true
		channel.unbind()
	})
	channel.bind('pusher:subscription_error', (error) => {
		log('presence', 'error during subscription', error)
		channel.unbind()
	})
	log('presence', 'awaiting subscription...', channel)
	store.userIsActive = false
}

export const unsubscribeToPresence = () => {
	const channel = getPresenceChannel()
	if (!channel) return
	log('presence', 'attempting to unsubscribe...')
	unsubscribeFromChannel(channel.name)
	log('presence', 'done unsubscribing')
	store.userIsActive = false
}

export const subscribeToPrivateUser = (pusherInstance = null) => {
	const userId = getMyUserId()
	const pusher = pusherInstance || getPusherInstance()
	if (!pusher) return
	if (!userId) {
		log('private.user', 'cannot subscribe, no userId', userId)
		return
	}
	const channelName = `private-user.${userId}`
	log('private.user', `subscribing to "${channelName}"...`)
	return pusher.subscribe(channelName)
}
